import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, PackageBadge, StatusPill, Loader } from "@/components/ui-bits";
import { Search, SlidersHorizontal, KeyRound, ChevronLeft, ChevronRight, Pencil } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [adjust, setAdjust] = useState(null);
  const [reset, setReset] = useState(null);
  const [edit, setEdit] = useState(null);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1, page_size: 25 });

  const load = (p = page, s = search) => API.get("/admin/users", { params: { page: p, page_size: 25, ...(s ? { search: s } : {}) } })
    .then((r) => { setUsers(r.data.users); setMeta({ total: r.data.total, total_pages: r.data.total_pages, page_size: r.data.page_size }); setPage(r.data.page); });
  useEffect(() => { load(1, ""); /* eslint-disable-next-line */ }, []);

  const doSearch = () => load(1, search);
  const goto = (p) => { if (p >= 1 && p <= meta.total_pages) load(p, search); };

  const applyAdjust = async () => {
    const amt = parseFloat(adjust.amount);
    if (!amt || amt <= 0) return toast.error("Enter an amount greater than 0");
    const signed = adjust.mode === "deduct" ? -Math.abs(amt) : Math.abs(amt);
    try {
      const r = await API.post("/admin/transactions/adjust", {
        user_id: adjust.user_id, wallet: adjust.wallet,
        amount: signed, note: adjust.note,
      });
      toast.success(r.data.message); setAdjust(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const applyReset = async () => {
    if ((reset.new_password || "").length < 5) return toast.error("Password must be at least 5 characters");
    try {
      const r = await API.post(`/admin/users/${reset.user_id}/reset-password`, { new_password: reset.new_password });
      toast.success(r.data.message); setReset(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const applyEdit = async () => {
    if (!(edit.name || "").trim()) return toast.error("Name cannot be empty");
    try {
      const r = await API.put(`/admin/users/${edit.user_id}`, { name: edit.name, email: edit.email, mobile: edit.mobile });
      toast.success(r.data.message); setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleAdvance = async (u) => {
    const next = !u.is_advance;
    setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, is_advance: next } : x));
    try {
      const r = await API.patch(`/admin/users/${u.id}/advance`, { is_advance: next });
      toast.success(r.data.message);
    } catch (e) {
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, is_advance: !next } : x));
      toast.error(apiError(e));
    }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleString() : "—";

  return (
    <div>
      <PageHeader title="User Management" subtitle="Search users, review wallets, packages and apply audited adjustments." testid="users-header" />

      <div className="glass rounded-2xl p-3 mb-6 flex gap-2">
        <div className="flex-1 flex items-center gap-2 px-3">
          <Search className="h-4 w-4 text-slate-500" />
          <input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()}
            placeholder="Search name, email or code..." className="w-full bg-transparent text-white outline-none py-2" />
        </div>
        <button data-testid="user-search-btn" onClick={doSearch} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold px-5 py-2 rounded-xl">Search</button>
      </div>

      {!users ? <Loader /> : (
        <div className="glass rounded-2xl p-2 sm:p-4" data-testid="users-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                <th className="py-3 px-3">Name</th><th className="py-3 px-3">Mobile</th><th className="py-3 px-3">Code</th><th className="py-3 px-3">Sponsor ID</th><th className="py-3 px-3">Package</th>
                <th className="py-3 px-3">Activated</th>
                <th className="py-3 px-3">Main</th><th className="py-3 px-3">Top-Up</th><th className="py-3 px-3">Earnings</th>
                <th className="py-3 px-3">Status</th><th className="py-3 px-3">Advance</th><th className="py-3 px-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {users.filter((u) => u.role !== "admin").map((u) => (
                  <tr key={u.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                    <td className="py-3 px-3"><div className="text-white">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                    <td className="py-3 px-3 font-num text-slate-300" data-testid={`mobile-${u.id}`}>{u.mobile || "—"}</td>
                    <td className="py-3 px-3 font-num text-slate-400">{u.referral_code}</td>
                    <td className="py-3 px-3 font-num text-cyan-300" data-testid={`sponsor-${u.id}`}>{u.sponsor_code || "—"}</td>
                    <td className="py-3 px-3"><PackageBadge id={u.package_id} /></td>
                    <td className="py-3 px-3 text-xs text-slate-400 whitespace-nowrap" data-testid={`activated-${u.id}`}>{u.package_id ? fmtDate(u.package_activated_at) : "—"}</td>
                    <td className="py-3 px-3 font-num text-white">{fmt(u.main_wallet)}</td>
                    <td className="py-3 px-3 font-num text-white">{fmt(u.topup_wallet)}</td>
                    <td className="py-3 px-3 font-num text-emerald-300">{fmt(u.total_earning)}</td>
                    <td className="py-3 px-3"><StatusPill status={u.status} /></td>
                    <td className="py-3 px-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" data-testid={`advance-toggle-${u.id}`} checked={!!u.is_advance}
                          onChange={() => toggleAdvance(u)}
                          className="h-4 w-4 rounded border-white/20 bg-white/5 accent-amber-400 cursor-pointer" />
                        {u.is_advance && <span data-testid={`advance-badge-${u.id}`} className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">Advance</span>}
                      </label>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`edit-${u.id}`} onClick={() => setEdit({ user_id: u.id, name: u.name, email: u.email || "", mobile: u.mobile || "" })}
                          className="inline-flex items-center gap-1 border border-white/15 hover:border-cyan-500/40 text-white px-3 py-1.5 rounded-lg text-xs"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                        <button data-testid={`adjust-${u.id}`} onClick={() => setAdjust({ user_id: u.id, name: u.name, wallet: "main_wallet", mode: "deduct", amount: "", note: "" })}
                          className="inline-flex items-center gap-1 border border-white/15 hover:border-emerald-500/40 text-white px-3 py-1.5 rounded-lg text-xs"><SlidersHorizontal className="h-3.5 w-3.5" /> Adjust</button>
                        <button data-testid={`reset-pw-${u.id}`} onClick={() => setReset({ user_id: u.id, name: u.name, new_password: "" })}
                          className="inline-flex items-center gap-1 border border-white/15 hover:border-amber-500/40 text-white px-3 py-1.5 rounded-lg text-xs"><KeyRound className="h-3.5 w-3.5" /> Reset PW</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3 pt-4 mt-2 border-t border-white/10" data-testid="users-pagination">
            <p className="text-xs text-slate-400">
              Showing <span className="font-num text-white">{users.length}</span> of{" "}
              <span className="font-num text-white" data-testid="users-total">{meta.total}</span> users
              {" "}· Page <span className="font-num text-white">{page}</span> / {meta.total_pages}
            </p>
            <div className="flex items-center gap-2">
              <button data-testid="users-prev" disabled={page <= 1} onClick={() => goto(page - 1)}
                className="inline-flex items-center gap-1 border border-white/15 hover:border-emerald-500/40 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg text-sm">
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <button data-testid="users-next" disabled={page >= meta.total_pages} onClick={() => goto(page + 1)}
                className="inline-flex items-center gap-1 border border-white/15 hover:border-emerald-500/40 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg text-sm">
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {adjust && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="adjust-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setAdjust(null)} />
          <div className="relative glass rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-display text-lg font-semibold text-white mb-1">Adjust Wallet — {adjust.name}</h3>
            <p className="text-xs text-slate-500 mb-4">Deduct moves money from the member's wallet into the Admin Fund. Add pulls money from the Admin Fund into the member's wallet. Both are audited.</p>
            <label className="text-xs text-slate-400">Action</label>
            <div className="mt-1 mb-3 grid grid-cols-2 gap-2">
              <button type="button" data-testid="adjust-mode-deduct" onClick={() => setAdjust({ ...adjust, mode: "deduct" })}
                className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${adjust.mode === "deduct" ? "bg-rose-500/20 border-rose-500/50 text-rose-200" : "border-white/10 text-slate-400 hover:text-white"}`}>Deduct (→ Admin Fund)</button>
              <button type="button" data-testid="adjust-mode-add" onClick={() => setAdjust({ ...adjust, mode: "add" })}
                className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${adjust.mode === "add" ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200" : "border-white/10 text-slate-400 hover:text-white"}`}>Add (← Admin Fund)</button>
            </div>
            <label className="text-xs text-slate-400">Wallet</label>
            <select value={adjust.wallet} onChange={(e) => setAdjust({ ...adjust, wallet: e.target.value })}
              className="mt-1 mb-3 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white outline-none">
              {["main_wallet", "topup_wallet", "deposit_wallet", "creator_wallet"].map((w) => <option key={w} value={w} className="bg-slate-900">{w}</option>)}
            </select>
            <input type="number" min="0" step="0.01" inputMode="decimal" placeholder="Amount" value={adjust.amount} onChange={(e) => setAdjust({ ...adjust, amount: e.target.value })}
              className="mb-3 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white outline-none" data-testid="adjust-amount" />
            <input placeholder="Audit note" value={adjust.note} onChange={(e) => setAdjust({ ...adjust, note: e.target.value })}
              className="mb-4 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white outline-none" data-testid="adjust-note" />
            <div className="flex gap-2">
              <button onClick={() => setAdjust(null)} className="flex-1 border border-white/15 text-white py-2.5 rounded-xl">Cancel</button>
              <button data-testid="adjust-apply" onClick={applyAdjust} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold py-2.5 rounded-xl">Apply</button>
            </div>
          </div>
        </div>
      )}

      {reset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="reset-pw-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setReset(null)} />
          <div className="relative glass rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-display text-lg font-semibold text-white mb-1">Reset Password — {reset.name}</h3>
            <p className="text-xs text-slate-500 mb-4">Set a new login password for this member (minimum 5 characters).</p>
            <label className="text-xs text-slate-400">New Password</label>
            <input type="text" placeholder="New password" value={reset.new_password} onChange={(e) => setReset({ ...reset, new_password: e.target.value })}
              className="mt-1 mb-4 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white outline-none" data-testid="reset-pw-input" />
            <div className="flex gap-2">
              <button onClick={() => setReset(null)} className="flex-1 border border-white/15 text-white py-2.5 rounded-xl">Cancel</button>
              <button data-testid="reset-pw-apply" onClick={applyReset} className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-2.5 rounded-xl">Reset Password</button>
            </div>
          </div>
        </div>
      )}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="edit-user-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEdit(null)} />
          <div className="relative glass rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-display text-lg font-semibold text-white mb-1">Edit Member</h3>
            <p className="text-xs text-slate-500 mb-4">Update the member's name, email and mobile number.</p>
            <label className="text-xs text-slate-400">Name</label>
            <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-name"
              className="mt-1 mb-3 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white outline-none" />
            <label className="text-xs text-slate-400">Email</label>
            <input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="edit-email"
              className="mt-1 mb-3 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white outline-none font-num" />
            <label className="text-xs text-slate-400">Mobile (with country code)</label>
            <input value={edit.mobile} onChange={(e) => setEdit({ ...edit, mobile: e.target.value })} data-testid="edit-mobile"
              className="mt-1 mb-4 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white outline-none font-num" />
            <div className="flex gap-2">
              <button onClick={() => setEdit(null)} className="flex-1 border border-white/15 text-white py-2.5 rounded-xl">Cancel</button>
              <button data-testid="edit-apply" onClick={applyEdit} className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold py-2.5 rounded-xl">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
